import React, { useState, useEffect } from 'react';
import apiClient from '../api/api';
import { FaPlus, FaEdit, FaTrash, FaCheckCircle, FaTimes, FaLayerGroup, FaImage, FaFont } from 'react-icons/fa';
import loadImage from '../assets/imageLoader';

function AdminCategoriesPage() {
    const [categories, setCategories] = useState([]);
    const [parents, setParents] = useState([]); // Top-level categories
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState({ Name: '', Description: '', Icon: '', parentId: '' });
    const [isEditing, setIsEditing] = useState(false);
    
    // Icon Handling
    const [iconType, setIconType] = useState('emoji'); // 'emoji' or 'image'
    const [uploadedIcon, setUploadedIcon] = useState(null);
    const [previewIcon, setPreviewIcon] = useState(null);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/categories');
            const allCats = res.data;
            setCategories(allCats);
            // Filter parents for the dropdown
            setParents(allCats.filter(c => !c.parentId));
        } catch (err) {
            setError('Failed to load categories.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleOpenModal = (cat = null) => {
        if (cat) {
            setCurrentCategory({ ...cat, parentId: cat.parentId || '' });
            setIsEditing(true);
            // Detect icon type
            if (cat.Icon && (cat.Icon.startsWith('http') || cat.Icon.startsWith('/uploads'))) {
                setIconType('image');
                setPreviewIcon(loadImage(cat.Icon));
            } else {
                setIconType('emoji');
                setPreviewIcon(null);
            }
        } else {
            setCurrentCategory({ Name: '', Description: '', Icon: '', parentId: '' });
            setIsEditing(false);
            setIconType('emoji');
            setPreviewIcon(null);
        }
        setUploadedIcon(null);
        setIsModalOpen(true);
    };

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            setUploadedIcon(e.target.files[0]);
            setPreviewIcon(URL.createObjectURL(e.target.files[0]));
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            let finalIcon = currentCategory.Icon;

            // Handle Image Upload if type is image and file is selected
            if (iconType === 'image' && uploadedIcon) {
                const imgData = new FormData();
                imgData.append('profilePicture', uploadedIcon); // Using existing endpoint which expects 'profilePicture' key
                const uploadRes = await apiClient.post('/upload/image', imgData, { headers: { 'Content-Type': 'multipart/form-data' } });
                finalIcon = uploadRes.data.filePath;
            } else if (iconType === 'image' && !uploadedIcon && !isEditing) {
                 // Creating new with image type but no image? default or error
                 // allow empty for now or keep existing if editing
            } else if (iconType === 'emoji') {
                // If switched to emoji, ensure we use the text value
                // currentCategory.Icon should hold the text input value
            }

            const payload = { ...currentCategory, Icon: finalIcon };

            if (isEditing) {
                await apiClient.put(`/categories/${currentCategory.id}`, payload);
            } else {
                await apiClient.post('/categories', payload);
            }
            setIsModalOpen(false);
            fetchCategories();
        } catch (err) {
            alert(err.response?.data?.message || 'Save failed');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure? This cannot be undone.")) return;
        try {
            await apiClient.delete(`/categories/${id}`);
            fetchCategories();
        } catch (err) {
            alert(err.response?.data?.message || 'Delete failed');
        }
    };

    if (loading) return <div className="p-10 text-center font-bold text-slate-500">Loading Categories...</div>;

    return (
        <div className="space-y-8 font-sans">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Category Management</h1>
                    <p className="text-slate-500 mt-1">Manage global job categories and skills.</p>
                </div>
                <button 
                    onClick={() => handleOpenModal()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-emerald-200 transition-all active:scale-95"
                >
                    <FaPlus /> Add New Category
                </button>
            </div>

            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                            <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Category Name</th>
                            <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Icon</th>
                            <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Description</th>
                            <th className="px-8 py-4 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {categories.map((cat) => (
                            <tr key={cat.id} className="hover:bg-slate-50 transition-colors group">
                                <td className="px-8 py-6">
                                    <div className="flex flex-col">
                                        <span className="font-black text-slate-800">{cat.Name}</span>
                                        <span className={`text-[10px] font-black uppercase w-fit px-2 py-0.5 rounded mt-1 ${cat.parentId ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                            {cat.parentId ? 'Sub-category' : 'Main Industry'}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-8 py-6">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold overflow-hidden border border-emerald-100">
                                        {cat.Icon && (cat.Icon.startsWith('http') || cat.Icon.startsWith('/uploads')) ? (
                                            <img src={loadImage(cat.Icon)} alt={cat.Name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-2xl">{cat.Icon || <FaLayerGroup />}</span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-8 py-6 text-slate-500 text-sm max-w-md">
                                    <p className="line-clamp-2">{cat.Description || 'No description provided.'}</p>
                                </td>
                                <td className="px-8 py-6 text-right space-x-2">
                                    <button 
                                        onClick={() => handleOpenModal(cat)}
                                        className="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(cat.id)}
                                        className="p-3 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden relative max-h-[90vh] flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white z-10">
                            <h2 className="text-xl font-black text-slate-800 tracking-tight">
                                {isEditing ? 'Edit Category' : 'Create Category'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                                <FaTimes size={18} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSave} className="p-6 space-y-5 overflow-y-auto custom-scrollbar flex-grow">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Category Name</label>
                                <input 
                                    required
                                    value={currentCategory.Name}
                                    onChange={e => setCurrentCategory({...currentCategory, Name: e.target.value})}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 ring-emerald-500 outline-none font-bold text-slate-700 transition-all text-sm"
                                    placeholder="e.g. Harvesting"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Classification (Parent)</label>
                                <select 
                                    value={currentCategory.parentId || ''}
                                    onChange={e => setCurrentCategory({...currentCategory, parentId: e.target.value || null})}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 ring-emerald-500 outline-none font-bold text-slate-700 transition-all text-sm appearance-none"
                                >
                                    <option value="">None (Make this a Main Industry)</option>
                                    {parents.filter(p => p.id !== currentCategory.id).map(p => (
                                        <option key={p.id} value={p.id}>{p.Name}</option>
                                    ))}
                                </select>
                                <p className="text-[10px] text-slate-400 mt-1 ml-1">Selecting a parent makes this a sub-service.</p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Icon Style</label>
                                <div className="flex gap-3">
                                    <button 
                                        type="button"
                                        onClick={() => setIconType('emoji')}
                                        className={`flex-1 py-2.5 rounded-xl border-2 font-bold text-xs flex items-center justify-center gap-2 transition-all ${iconType === 'emoji' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-100 text-slate-500 hover:bg-slate-50'}`}
                                    >
                                        <FaFont /> Text/Emoji
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setIconType('image')}
                                        className={`flex-1 py-2.5 rounded-xl border-2 font-bold text-xs flex items-center justify-center gap-2 transition-all ${iconType === 'image' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-100 text-slate-500 hover:bg-slate-50'}`}
                                    >
                                        <FaImage /> Image
                                    </button>
                                </div>

                                {iconType === 'emoji' ? (
                                    <div>
                                        <input 
                                            value={currentCategory.Icon}
                                            onChange={e => setCurrentCategory({...currentCategory, Icon: e.target.value})}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 ring-emerald-500 outline-none font-bold text-slate-700 transition-all text-xl text-center"
                                            placeholder="🚜"
                                        />
                                        <p className="text-[10px] text-slate-400 mt-1.5 ml-1">Example: 🚜, 🌾 or CSS class.</p>
                                    </div>
                                ) : (
                                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors relative">
                                        <input 
                                            type="file" 
                                            onChange={handleImageChange} 
                                            accept="image/*"
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        {previewIcon ? (
                                            <img src={previewIcon} alt="Preview" className="w-14 h-14 mx-auto object-cover rounded-lg shadow-sm" />
                                        ) : (
                                            <div className="text-slate-400">
                                                <FaImage className="mx-auto text-xl mb-1 opacity-50" />
                                                <p className="text-[10px] font-bold">Upload PNG/JPG</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Description</label>
                                <textarea 
                                    value={currentCategory.Description}
                                    onChange={e => setCurrentCategory({...currentCategory, Description: e.target.value})}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 ring-emerald-500 outline-none text-slate-600 transition-all h-24 text-sm"
                                    placeholder="Briefly describe..."
                                />
                            </div>

                            <div className="pt-2 flex gap-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-200 transition-all">
                                    Cancel
                                </button>
                                <button type="submit" className="flex-1 py-3.5 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 shadow-xl transition-all flex items-center justify-center gap-2">
                                    <FaCheckCircle /> Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminCategoriesPage;